import { formatDate } from '@seatix/shared-util-formatters';

const GENERIC_MESSAGE =
  'No pudimos completar la solicitud. Inténtalo de nuevo.';

/**
 * Forma mínima de un error HTTP tal como llega al store. Se comprueba
 * estructuralmente porque el error llega tipado como `unknown` y puede no venir
 * de la capa HTTP.
 */
interface HttpErrorLike {
  readonly status: number;
  readonly error: unknown;
}

function isHttpErrorLike(error: unknown): error is HttpErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as Record<string, unknown>)['status'] === 'number'
  );
}

/**
 * Códigos que `POST /api/auth/me/phone/{initiate,verify}` devuelve. A
 * diferencia del flujo público, aquí `error_code` viaja siempre en la raíz del
 * cuerpo — lo fija `JsonResponseMiddleware`.
 */
type MePhoneErrorCode =
  | 'invalid_phone'
  | 'invalid_code'
  | 'phone_taken'
  | 'account_pending_deletion';

function readBody(body: unknown): Record<string, unknown> | undefined {
  return typeof body === 'object' && body !== null
    ? (body as Record<string, unknown>)
    : undefined;
}

function extractErrorCode(body: unknown): string | undefined {
  const code = readBody(body)?.['error_code'];
  return typeof code === 'string' ? code : undefined;
}

function formatRecoverableUntil(body: unknown): string | undefined {
  const value = readBody(body)?.['recoverable_until'];

  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
    return undefined;
  }

  return formatDate(value, 'long');
}

/**
 * Mapea un error de la verificación de teléfono del usuario autenticado.
 *
 * Es **enumerante a propósito**, al revés que `mapWhatsAppOtpError`: quien
 * llega aquí ya tiene sesión y decirle que el número pertenece a otra cuenta es
 * la única forma de que pueda actuar. Por eso vive en su propio archivo — un
 * `case` extra en el mapper público rompería la propiedad no-enumerante que ese
 * flujo protege.
 */
export function mapMePhoneError(error: unknown): string {
  if (!isHttpErrorLike(error)) {
    return GENERIC_MESSAGE;
  }

  const code = extractErrorCode(error.error) as MePhoneErrorCode | undefined;

  switch (error.status) {
    case 409: {
      if (code === 'account_pending_deletion') {
        const recoverableUntil = formatRecoverableUntil(error.error);

        return recoverableUntil
          ? `Este número pertenece a una cuenta en proceso de eliminación, recuperable hasta el ${recoverableUntil}. Usa otro número o espera a esa fecha.`
          : 'Este número pertenece a una cuenta en proceso de eliminación. Usa otro número o inténtalo más tarde.';
      }

      // `phone_taken` y cualquier otro 409 de estas rutas describen el mismo
      // conflicto: el número ya está reclamado por otra cuenta.
      return 'Este número ya pertenece a otra cuenta. Usa otro número o inicia sesión con ese teléfono.';
    }
    case 422: {
      if (code === 'invalid_code') {
        return 'El código es inválido o expiró. Verifícalo e inténtalo de nuevo.';
      }

      // `invalid_phone` y el resto de validaciones de formato.
      return 'Revisa tu número de teléfono e inténtalo de nuevo.';
    }
    case 429:
      // La cubeta por cuenta (10 envíos/hora) es compartida entre `initiate` y
      // `verify`: reintentar por la otra vía no compra presupuesto.
      return 'Demasiados intentos. Espera un momento antes de volver a intentar.';
    case 503:
      return 'El servicio de verificación no está disponible. Inténtalo más tarde.';
    default:
      return GENERIC_MESSAGE;
  }
}
